import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  FlaskConical, Lightbulb, Microscope, TestTube, Rocket, CheckCircle2, 
  Plus, ArrowRight, TrendingUp, Target
} from "lucide-react";
import { toast } from "sonner";
import { API } from "@/config";

const STAGES = [
  { id: "idea", name: "Idea", icon: Lightbulb, color: "bg-yellow-100 text-yellow-700", borderColor: "border-yellow-400" },
  { id: "concept", name: "Concept", icon: FlaskConical, color: "bg-blue-100 text-blue-700", borderColor: "border-blue-400" },
  { id: "development", name: "Development", icon: Microscope, color: "bg-purple-100 text-purple-700", borderColor: "border-purple-400" },
  { id: "testing", name: "Testing", icon: TestTube, color: "bg-orange-100 text-orange-700", borderColor: "border-orange-400" },
  { id: "commercialization", name: "Commercialization", icon: Rocket, color: "bg-indigo-100 text-indigo-700", borderColor: "border-indigo-400" },
  { id: "launched", name: "Launched", icon: CheckCircle2, color: "bg-green-100 text-green-700", borderColor: "border-green-400" }
];

// Mock data
const MOCK_PROJECTS = [
  { id: "proj-001", name: "Organic Breakfast Line", description: "New line of organic breakfast items", stage: "development", item_count: 3, created_at: "2024-10-15" },
  { id: "proj-002", name: "Plant-Based Burritos", description: "Vegan-friendly burrito options", stage: "testing", item_count: 2, created_at: "2024-09-01" },
  { id: "proj-003", name: "Kids Meal Program", description: "Nutritious meals for school programs", stage: "concept", item_count: 4, created_at: "2024-11-20" },
  { id: "proj-004", name: "Premium Protein Bowl", description: "High-protein bowl with premium ingredients", stage: "idea", item_count: 1, created_at: "2025-01-05" },
  { id: "proj-005", name: "Gluten-Free Wraps", description: "Gluten-free tortilla wraps", stage: "commercialization", item_count: 2, created_at: "2024-06-15" },
  { id: "proj-006", name: "Mediterranean Bowl", description: "Mediterranean-inspired grain bowl", stage: "launched", item_count: 1, created_at: "2024-03-01" }
];

const RDDashboard = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newProject, setNewProject] = useState({
    name: "",
    description: "",
    stage: "idea",
    opportunity_id: null,
    opportunity_name: null
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [projectsRes, oppsRes] = await Promise.all([
        axios.get(`${API}/rd/projects`),
        axios.get(`${API}/crm/opportunities`)
      ]);
      setProjects(projectsRes.data);
      setOpportunities(oppsRes.data);
    } catch (error) {
      console.error('Failed to fetch data', error);
      setProjects(MOCK_PROJECTS);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async () => {
    try {
      const response = await axios.post(`${API}/rd/projects`, newProject);
      toast.success("Project created");
      setShowCreateModal(false);
      setNewProject({ name: "", description: "", stage: "idea", opportunity_id: null, opportunity_name: null });
      navigate(`/rd/projects/${response.data.id}`);
    } catch (error) {
      console.error('Failed to create project', error);
      const newProj = {
        ...newProject,
        id: `proj-${Date.now()}`,
        item_count: 0,
        created_at: new Date().toISOString().split('T')[0]
      };
      setProjects(prev => [newProj, ...prev]);
      toast.success("Project created");
      setShowCreateModal(false);
      setNewProject({ name: "", description: "", stage: "idea", opportunity_id: null, opportunity_name: null });
      navigate(`/rd/projects/${newProj.id}`);
    }
  };

  const handleOpportunitySelect = (oppId) => {
    if (oppId === "none") {
      setNewProject({ ...newProject, opportunity_id: null, opportunity_name: null });
    } else {
      const opp = opportunities.find(o => o.id === oppId);
      setNewProject({ 
        ...newProject, 
        opportunity_id: oppId, 
        opportunity_name: opp?.customer_name || null 
      });
    }
  };

  const getProjectsByStage = (stageId) => {
    return projects.filter(p => p.stage === stageId);
  };

  const getStageBadge = (stageId) => {
    const stage = STAGES.find(s => s.id === stageId);
    if (!stage) return null;
    return <Badge className={stage.color}>{stage.name}</Badge>;
  };

  // Stats
  const totalProjects = projects.length;
  const activeProjects = projects.filter(p => p.stage !== 'launched').length;
  const totalItems = projects.reduce((sum, p) => sum + (p.item_count || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <FlaskConical className="w-8 h-8" />
            R&D Dashboard
          </h1>
          <p className="text-gray-600">Track projects from idea to commercialization</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="w-4 h-4 mr-2" />
          New Project
        </Button>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-gray-500">Total Projects</div>
            <div className="text-2xl font-bold">{totalProjects}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-gray-500">Active Projects</div>
            <div className="text-2xl font-bold text-indigo-600">{activeProjects}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-gray-500">Total Items</div>
            <div className="text-2xl font-bold text-purple-600">{totalItems}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-gray-500">Launched This Year</div>
            <div className="text-2xl font-bold text-green-600">
              {projects.filter(p => p.stage === 'launched').length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pipeline View */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Development Pipeline
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            {STAGES.map((stage, index) => {
              const stageProjects = getProjectsByStage(stage.id);
              const Icon = stage.icon;
              return (
                <div key={stage.id} className="flex-1 flex items-center">
                  <div 
                    className={`flex-1 p-3 rounded-lg border-2 ${stage.borderColor} bg-white cursor-pointer hover:shadow-md transition-shadow`}
                    onClick={() => navigate(`/rd/projects?stage=${stage.id}`)}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className={`w-5 h-5 ${stage.color.split(' ')[1]}`} />
                      <span className="font-medium text-sm">{stage.name}</span>
                    </div>
                    <div className="text-2xl font-bold">{stageProjects.length}</div>
                    <div className="text-xs text-gray-500">
                      {stageProjects.reduce((sum, p) => sum + (p.item_count || 0), 0)} items
                    </div>
                  </div>
                  {index < STAGES.length - 1 && (
                    <ArrowRight className="w-5 h-5 text-gray-300 mx-1 flex-shrink-0" />
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Recent Projects */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Recent Projects</CardTitle>
          <Button variant="outline" size="sm" onClick={() => navigate('/rd/projects')}>
            View All
          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {projects.slice(0, 5).map(project => (
              <div 
                key={project.id}
                className="flex items-center justify-between p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => navigate(`/rd/projects/${project.id}`)}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <FlaskConical className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <div className="font-medium">{project.name}</div>
                    <div className="text-sm text-gray-500">{project.description}</div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-sm text-gray-500">{project.item_count} items</div>
                  {getStageBadge(project.stage)}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Create Project Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Project</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Project Name *</Label>
              <Input
                value={newProject.name}
                onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                placeholder="Enter project name"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={newProject.description}
                onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                placeholder="Describe the project..."
              />
            </div>
            <div>
              <Label>Link to CRM Opportunity</Label>
              <Select 
                value={newProject.opportunity_id || "none"} 
                onValueChange={handleOpportunitySelect}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select an opportunity (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No linked opportunity</SelectItem>
                  {opportunities
                    .filter((opp) => opp.id != null && String(opp.id) !== "")
                    .map(opp => (
                      <SelectItem key={opp.id} value={String(opp.id)}>
                      <div className="flex items-center gap-2">
                        <Target className="w-3 h-3 text-blue-600" />
                        {opp.customer_name} - {opp.description?.substring(0, 30)}...
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">Link this project to a CRM opportunity for tracking</p>
            </div>
            <div>
              <Label>Initial Stage</Label>
              <Select value={newProject.stage} onValueChange={(val) => setNewProject({ ...newProject, stage: val })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STAGES.filter((s) => s.id != null && String(s.id) !== "").map(stage => (
                    <SelectItem key={stage.id} value={String(stage.id)}>{stage.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button onClick={handleCreateProject} disabled={!newProject.name}>Create Project</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RDDashboard;
